import { SearchOutlined } from "@ant-design/icons";
import { useSelect } from "@refinedev/antd";
import { Button, Input, Modal, Table, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { CrudFilter } from "@refinedev/core";
import type { IUkhoNoticeRow, IVessel } from "interfaces";
import React, { useEffect, useState } from "react";
import { apiErrorMessage, useMatchNotice } from "./api";
import { matchDialogLine } from "./lib";
import { useDebouncedValue } from "./useDebouncedValue";

export interface MatchToShipModalProps {
  open: boolean;
  notice: IUkhoNoticeRow;
  onClose: () => void;
  /** Called after the match succeeded and its confirmation was shown. */
  onDone: () => void;
}

/** Name or IMO contains the text, as the vessel search of the New transfer form. */
const vesselSearchFilters = (searchText: string): CrudFilter[] =>
  searchText
    ? [
        {
          operator: "or",
          value: [
            { field: "name", operator: "contains", value: searchText },
            { field: "imoNumber", operator: "contains", value: searchText },
          ],
        },
      ]
    : [];

/**
 * Spec 11.3 match dialog: find the eMariner ship of an unmatched notice
 * (the search starts from the UKHO ship name) and match the notice to it by
 * hand.
 */
export const MatchToShipModal: React.FC<MatchToShipModalProps> = ({
  open,
  notice,
  onClose,
  onDone,
}) => {
  const [searchText, setSearchText] = useState(notice.licenceName);
  const vesselSearch = useDebouncedValue(searchText.trim());

  // Each opening starts the search from the UKHO ship name.
  useEffect(() => {
    if (open) {
      setSearchText(notice.licenceName);
    }
  }, [open, notice.licenceName]);

  const { query: vesselQuery } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "id",
    pagination: { mode: "server", pageSize: 20 },
    filters: vesselSearchFilters(vesselSearch),
    queryOptions: { enabled: vesselSearch !== "" },
  });
  const { mutate: matchNotice, mutation } = useMatchNotice();
  const foundVessels = vesselSearch ? vesselQuery.data?.data : [];

  const matchToVessel = (vessel: IVessel) => {
    matchNotice(
      { id: notice.id, vesselId: Number(vessel.id) },
      {
        onSuccess: () => {
          message.success(
            `Notice matched to ${vessel.name} by hand; its days now count for that ship`
          );
          onDone();
        },
        onError: (error) => {
          message.error(apiErrorMessage(error));
        },
      }
    );
  };

  const columns: ColumnsType<IVessel> = [
    { title: "Ship", dataIndex: "name", key: "name", width: 200, className: "wrap-text" },
    { title: "IMO", dataIndex: "imoNumber", key: "imoNumber", width: 120 },
    {
      title: "Customer",
      key: "customer",
      width: 200,
      className: "wrap-text",
      render: (_, vessel) => vessel.licence?.shipco?.registrationName ?? "",
    },
    {
      title: "",
      key: "match",
      width: 90,
      render: (_, vessel) => (
        <Button
          size="small"
          type="primary"
          disabled={mutation.isPending}
          onClick={() => matchToVessel(vessel)}
        >
          Match
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title={`Match notice ${notice.ukhoLicenceId} to a ship`}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Cancel</Button>}
      width={680}
    >
      <Typography.Paragraph type="secondary">
        {matchDialogLine(notice)}
      </Typography.Paragraph>
      <Input
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        prefix={<SearchOutlined />}
        allowClear
        style={{ marginBottom: 12 }}
      />
      <Table<IVessel>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={foundVessels}
        loading={vesselQuery.isFetching}
        pagination={false}
        locale={{ emptyText: "no match" }}
      />
    </Modal>
  );
};
